import type { Organization, Practitioner, Reference } from '@medplum/fhirtypes'
import { formatDateNumber } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import type { OrderStatus } from '@seen/core/models/orders/constants'
import { DotSeparator } from '@seen/ui'
import { IconArrowRight, IconCheck } from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'

export interface OrderOptionDisplayProps {
  seenIdentifier: string
  categoryLabel?: string
  title?: string
  status: OrderStatus
  requester?: Reference<Practitioner>
  performer?: Reference<Practitioner | Organization>
  signedBy?: Reference<Practitioner>
  signedDate?: string
  isSelected?: boolean
  children?: ReactNode
}

export function OrderOptionDisplay({
  seenIdentifier,
  categoryLabel,
  title,
  status,
  requester,
  performer,
  signedBy,
  signedDate,
  isSelected,
  children,
}: OrderOptionDisplayProps): JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-between">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <div className="p-0.5">
            <IconOrderStatus status={status} />
          </div>
          <div className="line-clamp-1 max-w-xl break-all">
            <span>
              {seenIdentifier}
              <DotSeparator />
              {!!categoryLabel && categoryLabel}
              {!!categoryLabel && !!title && ': '}
              {!!title && title}
              {!title && !categoryLabel && 'Untitled'}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-1">
          <div className="flex items-center gap-2">
            {(requester || performer) && (
              <div className="flex items-center gap-1">
                {requester && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Avatar profile={requester} size="icon" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Requester: {referenceDisplay(requester)}
                    </TooltipContent>
                  </Tooltip>
                )}
                {requester && performer && (
                  <IconArrowRight className="size-3.5 text-muted-foreground" />
                )}
                {performer && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Avatar profile={performer} size="icon" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Performer: {referenceDisplay(performer)}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}
            {children}
            {signedBy && signedDate && (
              <div className="text-xs text-foreground-secondary">
                Signed {formatDateNumber(signedDate)} <DotSeparator />{' '}
                {referenceDisplay(signedBy)}
              </div>
            )}
          </div>
        </div>
      </div>
      {isSelected && <IconCheck className="mr-0.5 size-4 shrink-0" />}
    </div>
  )
}
